import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";

import Card from "components/Layout/Card";
import DetailItem from "components/Layout/DetailItem";
import UserProfileDetailsCard from "components/Layout/UserProfileDetailsCard";
import { fetchUserProfileData } from "store/slices";
import { getDataFromLocalStorage, handleFileDownload } from "utils/helpers";
import { CVPath, paymentAbstractPath } from "utils/constants";
import EducationSection from "pages/Common/RequestRaisedAndReceived/MembershipTypeChange/DetailsComponents/EducationSection";
import Loader from "components/Layout/Loader";

const ViewProfile = () => {
    const { userId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const [userProfileData, setUserProfileData] = useState({});
    const [loading, setLoading] = useState(false);


    const getUserProfileData = async () => {
        setLoading(true);
        try {
            const response = await dispatch(fetchUserProfileData(userId));
            if (response?.data) setUserProfileData(response.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!['0', '1', '6'].includes(getDataFromLocalStorage('user_type'))) {
            navigate('/');
        }
        getUserProfileData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { user_details = {}, educational_details = {} } = userProfileData;

    const hasEducationData = Boolean(
        educational_details?.ug_course ||
        educational_details?.pg_course ||
        educational_details?.phd_course ||
        educational_details?.disciplines ||
        educational_details?.languages
    );

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <Loader size="sm" />
            </div>
        );
    }

    return (
        <div className="row cmt-5 g-2">
            <div className="col-12 col-md-5 col-lg-4">
                <UserProfileDetailsCard user_details={user_details} />
            </div>

            <div className="col-12 col-md-7 col-lg-8">
                <Card className="cp-24 cmb-10">
                    <div className="text-14-500-18 color-text-blue cmb-20">
                        Profile Details :
                    </div>

                    <div className="row">
                        <div className="col-md-4 col-sm-6 col-12">
                            <DetailItem label="Profile ID" value={user_details?.member_id || "-"} />
                        </div>

                        <div className="col-md-4 col-sm-6 col-12">
                            <DetailItem label="Membership ID" value={user_details?.member_id || "-"} />
                        </div>

                        <div className="col-md-4 col-sm-6 col-12">
                            <DetailItem
                                label="CV"
                                value={user_details?.cv}
                                type="url"
                                fileName={user_details?.cv}
                                handleFileDownload={handleFileDownload}
                                filePath={CVPath}
                                isView
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-4 col-sm-6 col-12">
                            <DetailItem
                                label="Join Date"
                                value={
                                    user_details?.join_date
                                        ? moment(user_details?.join_date).format('DD.MM.YYYY')
                                        : "-"
                                }
                            />
                        </div>

                        <div className="col-md-4 col-sm-6 col-12">
                            <DetailItem
                                label="Valid Till"
                                value={
                                    user_details?.valid_till
                                        ? moment(user_details?.valid_till).format('DD.MM.YYYY')
                                        : "-"
                                }
                            />
                        </div>
                    </div>
                </Card>

                <Card className="cp-24">
                    <div className="text-14-500-18 color-text-blue cmb-18">
                        Education Details :
                    </div>

                    {!hasEducationData ? (
                        <div className="text-center text-14-400-18 color-light-grey cpt-20 cpb-20">
                            No educational data found.
                        </div>
                    ) : (
                        <div className="row">
                            {/* UG Section */}
                            <EducationSection
                                labelPrefix="UG"
                                details={{
                                    course: educational_details?.ug_course,
                                    department: educational_details?.ug_department,
                                    university: educational_details?.ug_university,
                                    institution: educational_details?.ug_institution,
                                    year_of_completion: educational_details?.ug_year_of_completion,
                                }}
                            />

                            {/* PG Section */}
                            <EducationSection
                                labelPrefix="PG"
                                details={{
                                    course: educational_details?.pg_course,
                                    department: educational_details?.pg_department,
                                    university: educational_details?.pg_university,
                                    institution: educational_details?.pg_institution,
                                    year_of_completion: educational_details?.pg_year_of_completion,
                                }}
                            />

                            {/* Ph.D Section */}
                            <EducationSection
                                labelPrefix="Ph.D"
                                details={{
                                    course: educational_details?.phd_course,
                                    department: educational_details?.phd_department,
                                    university: educational_details?.phd_university,
                                    institution: educational_details?.phd_institution,
                                    year_of_completion: educational_details?.phd_year_of_completion,
                                }}
                            />

                            {/* Disciplines */}
                            {educational_details?.disciplines && (
                                <>
                                    <div className="text-14-500-18 color-text-blue cmt-15">
                                        Disciplines :
                                    </div>
                                    <div className="d-flex flex-wrap gap-2 cmt-16">
                                        {educational_details.disciplines.split(",").map((disc, i) => (
                                            <div
                                                key={i}
                                                className="cps-12 cpe-12 cpb-6 cpt-6 text-14-400-18 color-black-olive bg-F3F4F8 br-5"
                                            >
                                                {disc.trim()}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* Languages */}
                            {educational_details?.languages && (
                                <>
                                    <div className="text-14-500-18 color-text-blue cmt-19">
                                        Languages :
                                    </div>
                                    <div className="d-flex flex-wrap gap-2 cmt-16">
                                        {educational_details.languages.split(",").map((lang, i) => (
                                            <div
                                                key={i}
                                                className="cps-12 cpe-12 cpb-6 cpt-6 text-14-400-18 color-black-olive bg-F3F4F8 br-5"
                                            >
                                                {lang.trim()}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default ViewProfile;
